<script setup>
/* global axios */
import {
  ref,
  computed,
  reactive,
  onMounted,
  onActivated,
  watch,
  nextTick,
} from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useSegmentsStore } from 'segments/store/index.js';
import {
  findComponentByType,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
} from 'dashboard/helper/templateHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import CreateTemplateModal from 'flow_builder/components/CreateTemplateModal.vue';
import WhatsappTemplatePreview from 'flow_builder/components/WhatsappTemplatePreview.vue';
import TemplateProductPicker from 'flow_builder/components/panels/action-forms/TemplateProductPicker.vue';

const props = defineProps({
  channelType: { type: String, required: true },
});

const router = useRouter();
const { t } = useI18n();
const { accountId, accountScopedRoute } = useAccount();
const store = useStore();
const segmentsStore = useSegmentsStore();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const whatsAppInboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const smsInboxes = useMapGetter('inboxes/getSMSInboxes');
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const currentStep = ref(1);
const templateParserRef = ref(null);
const templateProductConfig = ref({ source: 'catalog', products: [] });
const isSyncing = ref(false);
const showCreateTemplateModal = ref(false);
const templateHeaderMediaUrl = ref('');

const SCHEDULE_TYPE_OPTIONS = computed(() => [
  { id: 'once', name: t('CAMPAIGN.WIZARD.SCHEDULE_TYPES.ONCE') },
  { id: 'smart', name: t('CAMPAIGN.WIZARD.SCHEDULE_TYPES.SMART') },
  { id: 'daily', name: t('CAMPAIGN.WIZARD.SCHEDULE_TYPES.DAILY') },
  { id: 'weekly', name: t('CAMPAIGN.WIZARD.SCHEDULE_TYPES.WEEKLY') },
  { id: 'monthly', name: t('CAMPAIGN.WIZARD.SCHEDULE_TYPES.MONTHLY') },
]);
const WEEKDAY_OPTIONS = computed(() => [
  { id: 1, name: t('CAMPAIGN.WIZARD.WEEKDAYS.MON') },
  { id: 2, name: t('CAMPAIGN.WIZARD.WEEKDAYS.TUE') },
  { id: 3, name: t('CAMPAIGN.WIZARD.WEEKDAYS.WED') },
  { id: 4, name: t('CAMPAIGN.WIZARD.WEEKDAYS.THU') },
  { id: 5, name: t('CAMPAIGN.WIZARD.WEEKDAYS.FRI') },
  { id: 6, name: t('CAMPAIGN.WIZARD.WEEKDAYS.SAT') },
  { id: 0, name: t('CAMPAIGN.WIZARD.WEEKDAYS.SUN') },
]);
const MONTHDAY_OPTIONS = computed(() =>
  Array.from({ length: 31 }, (_, i) => ({
    id: i + 1,
    name: t('CAMPAIGN.WIZARD.MONTH_DAY', { day: i + 1 }),
  }))
);

function utcTimeToLocal(utcTime) {
  if (!utcTime) return '';
  const [h, m] = utcTime.split(':').map(Number);
  const d = new Date();
  d.setUTCHours(h, m, 0, 0);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
function localTimeToUtc(localTime) {
  if (!localTime) return '';
  const [h, m] = localTime.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

const form = reactive({
  title: '',
  inboxId: null,
  templateId: null,
  message: '',
  selectedAudience: [],
  scheduledAt: '',
  scheduleType: 'once',
  scheduleTime: '',
  scheduleDay: null,
});

const isWhatsApp = computed(() => props.channelType === 'whatsapp');
const isCreating = computed(() => uiFlags.value.isCreating);

const STEPS = computed(() => [
  {
    id: 1,
    title: t('CAMPAIGN.WIZARD.STEPS.CONFIG_TITLE'),
    body: t('CAMPAIGN.WIZARD.STEPS.CONFIG_BODY'),
  },
  {
    id: 2,
    title: t('CAMPAIGN.WIZARD.STEPS.MESSAGE_TITLE'),
    body: isWhatsApp.value
      ? t('CAMPAIGN.WIZARD.STEPS.MESSAGE_BODY_WHATSAPP')
      : t('CAMPAIGN.WIZARD.STEPS.MESSAGE_BODY_SMS'),
  },
  {
    id: 3,
    title: t('CAMPAIGN.WIZARD.STEPS.AUDIENCE_TITLE'),
    body: t('CAMPAIGN.WIZARD.STEPS.AUDIENCE_BODY'),
  },
  {
    id: 4,
    title: t('CAMPAIGN.WIZARD.STEPS.CONFIRM_TITLE'),
    body: t('CAMPAIGN.WIZARD.STEPS.CONFIRM_BODY'),
  },
]);

const inboxes = computed(() =>
  isWhatsApp.value ? whatsAppInboxes.value : smsInboxes.value
);

const inboxOptions = computed(() =>
  (inboxes.value || []).map(i => ({ value: i.id, label: i.name }))
);

const templateOptions = computed(() => {
  if (!form.inboxId || !isWhatsApp.value) return [];
  const templates = getFilteredWhatsAppTemplates.value(form.inboxId) || [];
  return templates.map(tpl => ({
    value: tpl.id,
    label:
      tpl.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) +
      ` (${tpl.language || 'en'})`,
    template: tpl,
  }));
});

const selectedTemplate = computed(
  () =>
    templateOptions.value.find(o => o.value === form.templateId)?.template ||
    null
);

const templateHeader = computed(() => {
  if (!selectedTemplate.value) return { format: 'NONE' };
  const header = findComponentByType(
    selectedTemplate.value,
    COMPONENT_TYPES.HEADER
  );
  if (!header) return { format: 'NONE' };
  return {
    format: (header.format || 'NONE').toUpperCase(),
    text: header.text || '',
  };
});

const templateBody = computed(() => {
  if (!selectedTemplate.value) return '';
  return (
    findComponentByType(selectedTemplate.value, COMPONENT_TYPES.BODY)?.text ??
    ''
  );
});

const templateFooter = computed(() => {
  if (!selectedTemplate.value) return '';
  return findComponentByType(selectedTemplate.value, 'FOOTER')?.text ?? '';
});

const templateButtons = computed(() => {
  if (!selectedTemplate.value) return [];
  const buttonsComp = findComponentByType(
    selectedTemplate.value,
    COMPONENT_TYPES.BUTTONS
  );
  if (!buttonsComp?.buttons) return [];
  return buttonsComp.buttons.map(btn => ({ type: btn.type, text: btn.text }));
});

const isSpmTemplate = computed(() =>
  templateButtons.value.some(b => b.type?.toUpperCase() === 'SPM')
);
const isMpmTemplate = computed(() =>
  templateButtons.value.some(b => b.type?.toUpperCase() === 'MPM')
);
const isCarouselTemplate = computed(() =>
  (selectedTemplate.value?.components ?? []).some(
    c => c.type?.toUpperCase() === 'CAROUSEL'
  )
);
const isSpmUrlTemplate = computed(() => {
  const components = selectedTemplate.value?.components ?? [];
  const header = components.find(c => c.type?.toUpperCase() === 'HEADER');
  if (header?.format?.toUpperCase() !== 'PRODUCT') return false;
  if (isCarouselTemplate.value) return false;
  return templateButtons.value.some(b => b.type?.toUpperCase() === 'URL');
});
const needsProductConfig = computed(
  () =>
    isSpmTemplate.value ||
    isMpmTemplate.value ||
    isCarouselTemplate.value ||
    isSpmUrlTemplate.value
);
const productPickerMode = computed(() =>
  isSpmTemplate.value || isSpmUrlTemplate.value ? 'single' : 'multi'
);
const productPickerMin = computed(() => (isCarouselTemplate.value ? 2 : 1));
const productPickerMax = computed(() => (isCarouselTemplate.value ? 10 : 30));

async function syncTemplates() {
  if (!form.inboxId) return;
  isSyncing.value = true;
  try {
    await store.dispatch('inboxes/syncTemplates', form.inboxId);
    await store.dispatch('inboxes/get');
    useAlert(t('CAMPAIGN.WIZARD.ALERTS.SYNC_SUCCESS'));
  } catch (e) {
    useAlert(e.message || t('CAMPAIGN.WIZARD.ALERTS.SYNC_ERROR'));
  } finally {
    isSyncing.value = false;
  }
}

async function onTemplateCreated(template) {
  showCreateTemplateModal.value = false;
  try {
    await store.dispatch('inboxes/syncTemplates', form.inboxId);
    await store.dispatch('inboxes/get');
  } catch (_e) {
    // template recém-criado pode estar em PENDING; ignora erros de sync
  }
  const synced = getFilteredWhatsAppTemplates
    .value(form.inboxId)
    .find(tpl => tpl.name === template?.name);
  if (synced) form.templateId = synced.id;
}

async function fetchTemplateHeaderMedia(template) {
  templateHeaderMediaUrl.value = '';
  if (!template || !form.inboxId) return;
  const header = findComponentByType(template, COMPONENT_TYPES.HEADER);
  const format = header?.format?.toUpperCase();
  if (!format || !MEDIA_FORMATS.includes(format)) return;
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/inboxes/${form.inboxId}/whatsapp_templates/asset`,
      { params: { name: template.name, language: template.language } }
    );
    const url = data?.header_media_url ?? '';
    templateHeaderMediaUrl.value = url;
    if (!url) return;
    // Aguarda o parser remontar com o novo template antes de injetar a URL.
    await nextTick();
    templateParserRef.value?.updateMediaUrl(url);
  } catch (_e) {
    // sem asset cadastrado — segue sem pré-preencher
  }
}

const segmentOptions = computed(() =>
  segmentsStore.segments.map(s => ({ value: s.id, label: s.name }))
);

const totalContacts = computed(() =>
  segmentsStore.segments
    .filter(s => form.selectedAudience.includes(s.id))
    .reduce((sum, s) => sum + (s.contacts_count || 0), 0)
);

const currentDateTime = computed(() => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
});

const selectedInboxName = computed(
  () => inboxes.value?.find(i => i.id === form.inboxId)?.name || '—'
);

const selectedSegmentNames = computed(
  () =>
    segmentsStore.segments
      .filter(s => form.selectedAudience.includes(s.id))
      .map(s => s.name)
      .join(', ') || '—'
);

const selectedScheduleType = computed(
  () =>
    SCHEDULE_TYPE_OPTIONS.value.find(o => o.id === form.scheduleType) ??
    SCHEDULE_TYPE_OPTIONS.value[0]
);
const selectedWeekday = computed(
  () =>
    WEEKDAY_OPTIONS.value.find(o => o.id === form.scheduleDay) ??
    WEEKDAY_OPTIONS.value[0]
);
const selectedMonthday = computed(
  () =>
    MONTHDAY_OPTIONS.value.find(o => o.id === form.scheduleDay) ??
    MONTHDAY_OPTIONS.value[0]
);

const scheduleValid = computed(() => {
  if (form.scheduleType === 'once') return !!form.scheduledAt;
  if (form.scheduleType === 'smart') return true;
  if (form.scheduleType === 'daily') return !!form.scheduleTime;
  if (form.scheduleType === 'weekly')
    return !!form.scheduleTime && form.scheduleDay !== null;
  if (form.scheduleType === 'monthly')
    return !!form.scheduleTime && form.scheduleDay !== null;
  return false;
});

const canProceed = computed(() => {
  if (currentStep.value === 1) return !!form.title.trim() && !!form.inboxId;
  if (currentStep.value === 2)
    return isWhatsApp.value ? !!form.templateId : !!form.message.trim();
  if (currentStep.value === 3) return form.selectedAudience.length > 0;
  return scheduleValid.value;
});

const stepTitle = computed(() => {
  const titles = [
    t('CAMPAIGN.WIZARD.HEADINGS.STEP1_TITLE'),
    t('CAMPAIGN.WIZARD.HEADINGS.STEP2_TITLE'),
    t('CAMPAIGN.WIZARD.HEADINGS.STEP3_TITLE'),
    t('CAMPAIGN.WIZARD.HEADINGS.STEP4_TITLE'),
  ];
  return titles[currentStep.value - 1];
});

const stepDesc = computed(() => {
  const descs = [
    t('CAMPAIGN.WIZARD.HEADINGS.STEP1_DESC'),
    isWhatsApp.value
      ? t('CAMPAIGN.WIZARD.HEADINGS.STEP2_DESC_WHATSAPP')
      : t('CAMPAIGN.WIZARD.HEADINGS.STEP2_DESC_SMS'),
    t('CAMPAIGN.WIZARD.HEADINGS.STEP3_DESC'),
    t('CAMPAIGN.WIZARD.HEADINGS.STEP4_DESC'),
  ];
  return descs[currentStep.value - 1];
});

const { localeTag } = useDateLocale();

function formatDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString(localeTag.value);
}

const scheduleLabel = computed(() => {
  if (form.scheduleType === 'once') return formatDate(form.scheduledAt);
  if (form.scheduleType === 'smart')
    return t('CAMPAIGN.WIZARD.SMART_SCHEDULE_LABEL');
  const label =
    SCHEDULE_TYPE_OPTIONS.value.find(o => o.id === form.scheduleType)?.name ??
    '';
  const timeLocal = utcTimeToLocal(form.scheduleTime);
  if (form.scheduleType === 'daily')
    return t('CAMPAIGN.WIZARD.SCHEDULE_SUMMARY.DAILY', {
      label,
      time: timeLocal,
    });
  if (form.scheduleType === 'weekly') {
    const day =
      WEEKDAY_OPTIONS.value.find(o => o.id === form.scheduleDay)?.name ?? '';
    return t('CAMPAIGN.WIZARD.SCHEDULE_SUMMARY.RECURRING', {
      label,
      day,
      time: timeLocal,
    });
  }
  if (form.scheduleType === 'monthly') {
    const day =
      MONTHDAY_OPTIONS.value.find(o => o.id === form.scheduleDay)?.name ?? '';
    return t('CAMPAIGN.WIZARD.SCHEDULE_SUMMARY.RECURRING', {
      label,
      day,
      time: timeLocal,
    });
  }
  return '—';
});

function resetForm() {
  Object.assign(form, {
    title: '',
    inboxId: null,
    templateId: null,
    message: '',
    selectedAudience: [],
    scheduledAt: '',
    scheduleType: 'once',
    scheduleTime: '',
    scheduleDay: null,
  });
  currentStep.value = 1;
}

onMounted(() => {
  if (!segmentsStore.segments.length) segmentsStore.fetchAll();
});

onActivated(resetForm);

function cancel() {
  router.push(
    accountScopedRoute(
      isWhatsApp.value ? 'campaigns_whatsapp_index' : 'campaigns_sms_index'
    )
  );
}

function goBack() {
  if (currentStep.value > 1) {
    currentStep.value -= 1;
  } else {
    cancel();
  }
}

function goNext() {
  if (canProceed.value && currentStep.value < 4) currentStep.value += 1;
}

async function submit() {
  const parserData = templateParserRef.value;

  const isSmart = form.scheduleType === 'smart';
  const triggerRules = {
    schedule_type: isSmart ? 'once' : form.scheduleType,
  };
  let scheduledAt = null;
  if (form.scheduleType === 'once') {
    scheduledAt = form.scheduledAt
      ? new Date(form.scheduledAt).toISOString()
      : null;
  } else if (isSmart) {
    scheduledAt = new Date().toISOString();
  } else {
    triggerRules.schedule_time = localTimeToUtc(form.scheduleTime);
    if (form.scheduleType !== 'daily')
      triggerRules.schedule_day = form.scheduleDay;
  }

  const payload = {
    title: form.title.trim(),
    inbox_id: form.inboxId,
    scheduled_at: scheduledAt,
    smart_schedule: isSmart,
    trigger_rules: triggerRules,
    audience: form.selectedAudience.map(id => ({ id, type: 'Segment' })),
  };

  if (isWhatsApp.value) {
    payload.message = parserData?.renderedTemplate || '';
    payload.template_params = {
      name: selectedTemplate.value?.name || '',
      namespace: selectedTemplate.value?.namespace || '',
      category: selectedTemplate.value?.category || 'UTILITY',
      language: selectedTemplate.value?.language || 'en_US',
      processed_params: parserData?.processedParams || {},
    };
    if (needsProductConfig.value) {
      payload.template_params.template_product_config =
        templateProductConfig.value;
    }
  } else {
    payload.message = form.message.trim();
  }

  try {
    await store.dispatch('campaigns/create', payload);
    useAlert(t('CAMPAIGN.WIZARD.ALERTS.CREATE_SUCCESS'));
    router.push(
      accountScopedRoute(
        isWhatsApp.value ? 'campaigns_whatsapp_index' : 'campaigns_sms_index'
      )
    );
  } catch {
    useAlert(t('CAMPAIGN.WIZARD.ALERTS.CREATE_ERROR'));
  }
}

watch(
  () => form.inboxId,
  () => {
    form.templateId = null;
    templateHeaderMediaUrl.value = '';
  }
);

watch(selectedTemplate, () => {
  templateProductConfig.value = { source: 'catalog', products: [] };
});

watch(
  () => selectedTemplate.value,
  template => fetchTemplateHeaderMedia(template)
);
</script>

<!-- eslint-disable vue/html-closing-bracket-newline -->
<template>
  <div class="flex flex-col gap-6 py-8 max-w-5xl mx-auto w-full px-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <button
          class="flex items-center justify-center size-8 rounded-lg hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @click="cancel"
        >
          <span class="i-lucide-arrow-left size-4" />
        </button>
        <h1 class="text-heading-1 text-n-slate-12">
          {{
            isWhatsApp
              ? t('CAMPAIGN.WIZARD.TITLE.WHATSAPP')
              : t('CAMPAIGN.WIZARD.TITLE.SMS')
          }}
        </h1>
      </div>
    </div>

    <div
      class="grid grid-cols-1 lg:grid-cols-8 lg:divide-x lg:divide-n-weak rounded-xl border border-n-weak w-full"
    >
      <!-- Wizard sidebar — idêntico ao woot-wizard -->
      <div class="hidden lg:block col-span-2 h-fit py-8 px-6">
        <div
          v-for="step in STEPS"
          :key="step.id"
          class="flex items-start gap-6 relative after:content-[''] after:absolute after:w-0.5 after:h-full after:top-5 ltr:after:left-4 rtl:after:right-4 before:content-[''] before:absolute before:w-0.5 before:h-4 before:top-0 ltr:before:left-4 rtl:before:right-4 last:after:hidden last:before:hidden after:bg-n-slate-3 before:bg-n-slate-3"
        >
          <div
            class="rounded-2xl flex-shrink-0 size-8 border-2 border-n-slate-3 flex items-center justify-center z-10 transition-all duration-300 ease-in-out"
            :class="{
              'bg-n-slate-3': currentStep === step.id || step.id < currentStep,
              'bg-n-background': step.id > currentStep,
            }"
          >
            <span
              v-if="step.id >= currentStep"
              class="text-xs font-bold transition-colors duration-300"
              :class="
                currentStep === step.id ? 'text-n-blue-11' : 'text-n-slate-11'
              "
            >
              {{ step.id }}
            </span>
            <span v-else class="i-lucide-check text-n-slate-11 size-4" />
          </div>
          <div class="flex flex-col items-start gap-1.5 pb-10 pt-1">
            <h3
              class="text-sm font-medium overflow-hidden whitespace-nowrap mt-0.5 text-ellipsis leading-tight"
              :class="
                currentStep === step.id ? 'text-n-blue-11' : 'text-n-slate-12'
              "
            >
              {{ step.title }}
            </h3>
            <p class="m-0 mt-1.5 text-sm text-n-slate-11">{{ step.body }}</p>
          </div>
        </div>
      </div>

      <!-- Conteúdo do step -->
      <div class="col-span-6 w-full p-8">
        <!-- Cabeçalho do step -->
        <div class="flex flex-col gap-1.5 w-full items-start mb-6">
          <h2 class="text-heading-1 text-n-slate-12">{{ stepTitle }}</h2>
          <p class="text-body-main text-n-slate-11">{{ stepDesc }}</p>
        </div>

        <!-- Step 1: Configuração -->
        <div v-show="currentStep === 1" class="flex flex-col gap-4 max-w-lg">
          <Input
            v-model="form.title"
            :label="t('CAMPAIGN.WIZARD.FIELDS.NAME_LABEL')"
            :placeholder="t('CAMPAIGN.WIZARD.FIELDS.NAME_PLACEHOLDER')"
            autocomplete="off"
          />
          <div class="flex flex-col gap-1">
            <label class="mb-0.5 text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WIZARD.FIELDS.INBOX_LABEL') }}
            </label>
            <ComboBox
              v-model="form.inboxId"
              :options="inboxOptions"
              :placeholder="t('CAMPAIGN.WIZARD.FIELDS.INBOX_PLACEHOLDER')"
              class="[&>div>button]:bg-n-alpha-black2"
            />
          </div>
        </div>

        <!-- Step 2: Mensagem -->
        <div v-show="currentStep === 2" class="flex flex-col gap-4 max-w-lg">
          <template v-if="isWhatsApp">
            <div class="flex flex-col gap-1">
              <div class="flex items-center justify-between mb-0.5">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ t('CAMPAIGN.WIZARD.FIELDS.TEMPLATE_LABEL') }}
                </label>
                <div class="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    color="blue"
                    size="xs"
                    icon="i-lucide-refresh-cw"
                    :label="t('CAMPAIGN.WIZARD.ACTIONS.SYNC')"
                    :is-loading="isSyncing"
                    :disabled="!form.inboxId || isSyncing"
                    @click="syncTemplates"
                  />
                  <Button
                    variant="ghost"
                    color="blue"
                    size="xs"
                    icon="i-lucide-plus"
                    :label="t('CAMPAIGN.WIZARD.ACTIONS.CREATE_TEMPLATE')"
                    :disabled="!form.inboxId"
                    @click="showCreateTemplateModal = true"
                  />
                </div>
              </div>
              <ComboBox
                v-model="form.templateId"
                :options="templateOptions"
                :placeholder="t('CAMPAIGN.WIZARD.FIELDS.TEMPLATE_PLACEHOLDER')"
                class="[&>div>button]:bg-n-alpha-black2"
              />
            </div>
            <CreateTemplateModal
              :show="showCreateTemplateModal"
              :inbox-id="form.inboxId"
              @close="showCreateTemplateModal = false"
              @created="onTemplateCreated"
            />
            <WhatsappTemplatePreview
              v-if="selectedTemplate"
              :header="templateHeader"
              :body="templateBody"
              :footer="templateFooter"
              :buttons="templateButtons"
              :header-preview-url="templateHeaderMediaUrl"
            />
            <WhatsAppTemplateParser
              v-if="selectedTemplate"
              ref="templateParserRef"
              :template="selectedTemplate"
              :show-preview="false"
            />
            <div v-if="needsProductConfig" class="flex flex-col gap-1.5">
              <label class="mb-0.5 text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WIZARD.FIELDS.PRODUCTS_LABEL') }}
              </label>
              <TemplateProductPicker
                :model-value="templateProductConfig"
                :mode="productPickerMode"
                :min="productPickerMin"
                :max="productPickerMax"
                :available-variables="[]"
                @update:model-value="templateProductConfig = $event"
              />
            </div>
          </template>
          <TextArea
            v-else
            v-model="form.message"
            :label="t('CAMPAIGN.WIZARD.FIELDS.MESSAGE_LABEL')"
            :placeholder="t('CAMPAIGN.WIZARD.FIELDS.MESSAGE_PLACEHOLDER')"
            show-character-count
          />
        </div>

        <!-- Step 3: Audiência -->
        <div v-show="currentStep === 3" class="flex flex-col gap-4 max-w-lg">
          <div class="flex flex-col gap-1">
            <label class="mb-0.5 text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WIZARD.FIELDS.SEGMENTS_LABEL') }}
            </label>
            <TagMultiSelectComboBox
              v-model="form.selectedAudience"
              :options="segmentOptions"
              :label="t('CAMPAIGN.WIZARD.FIELDS.SEGMENTS_LABEL')"
              :placeholder="t('CAMPAIGN.WIZARD.FIELDS.SEGMENTS_PLACEHOLDER')"
              class="[&>div>button]:bg-n-alpha-black2"
            />
          </div>
          <div
            v-if="form.selectedAudience.length"
            class="rounded-lg border border-n-slate-4 p-4 flex items-center gap-3"
          >
            <span class="i-lucide-users size-4 text-n-slate-9 shrink-0" />
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{
                  t(
                    'CAMPAIGN.WIZARD.MATCHING_CONTACTS',
                    { count: totalContacts.toLocaleString(localeTag.value) },
                    totalContacts
                  )
                }}
              </p>
              <p class="text-xs text-n-slate-11 mt-0.5">
                {{ selectedSegmentNames }}
              </p>
            </div>
          </div>
        </div>

        <!-- Step 4: Confirmação -->
        <div v-show="currentStep === 4" class="flex flex-col gap-4 max-w-lg">
          <!-- Tipo de agendamento -->
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WIZARD.FIELDS.PERIODICITY') }}
            </span>
            <div class="multiselect-wrap--small">
              <MultiselectDropdown
                :options="SCHEDULE_TYPE_OPTIONS"
                :selected-item="selectedScheduleType"
                :has-thumbnail="false"
                hide-search
                @select="
                  v => {
                    form.scheduleType = v?.id ?? 'once';
                    form.scheduleDay = null;
                    form.scheduleTime = '';
                    form.scheduledAt = '';
                  }
                "
              />
            </div>
          </div>

          <!-- Uma vez: datetime -->
          <Input
            v-if="form.scheduleType === 'once'"
            v-model="form.scheduledAt"
            :label="t('CAMPAIGN.WIZARD.FIELDS.SEND_DATETIME')"
            type="datetime-local"
            :min="currentDateTime"
          />

          <!-- Smart: hint, sem inputs -->
          <p
            v-else-if="form.scheduleType === 'smart'"
            class="text-xs text-n-slate-11 rounded-lg bg-n-alpha-2 px-3 py-2 border border-n-weak"
          >
            {{ t('CAMPAIGN.WIZARD.SMART_HINT') }}
          </p>

          <!-- Recorrente: hora -->
          <template v-else>
            <div class="flex flex-col gap-1.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WIZARD.FIELDS.SEND_TIME') }}
              </span>
              <input
                type="time"
                class="h-9 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                :value="utcTimeToLocal(form.scheduleTime)"
                @change="
                  form.scheduleTime = localTimeToUtc($event.target.value)
                "
              />
            </div>

            <!-- Semanal: dia da semana -->
            <div
              v-if="form.scheduleType === 'weekly'"
              class="flex flex-col gap-1.5"
            >
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WIZARD.FIELDS.WEEKDAY') }}
              </span>
              <div class="multiselect-wrap--small">
                <MultiselectDropdown
                  :options="WEEKDAY_OPTIONS"
                  :selected-item="selectedWeekday"
                  :has-thumbnail="false"
                  hide-search
                  @select="v => (form.scheduleDay = v?.id ?? 1)"
                />
              </div>
            </div>

            <!-- Mensal: dia do mês -->
            <div
              v-if="form.scheduleType === 'monthly'"
              class="flex flex-col gap-1.5"
            >
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WIZARD.FIELDS.MONTHDAY') }}
              </span>
              <div class="multiselect-wrap--small">
                <MultiselectDropdown
                  :options="MONTHDAY_OPTIONS"
                  :selected-item="selectedMonthday"
                  :has-thumbnail="false"
                  hide-search
                  @select="v => (form.scheduleDay = v?.id ?? 1)"
                />
              </div>
            </div>
          </template>
          <div
            class="rounded-lg border border-n-slate-4 divide-y divide-n-slate-4 mt-2"
          >
            <div class="flex items-center justify-between px-4 py-3">
              <span class="text-sm text-n-slate-11">
                {{ t('CAMPAIGN.WIZARD.SUMMARY.CAMPAIGN') }}
              </span>
              <span class="text-sm font-medium text-n-slate-12">{{
                form.title
              }}</span>
            </div>
            <div class="flex items-center justify-between px-4 py-3">
              <span class="text-sm text-n-slate-11">
                {{ t('CAMPAIGN.WIZARD.SUMMARY.INBOX') }}
              </span>
              <span class="text-sm font-medium text-n-slate-12">{{
                selectedInboxName
              }}</span>
            </div>
            <div
              v-if="isWhatsApp"
              class="flex items-center justify-between px-4 py-3"
            >
              <span class="text-sm text-n-slate-11">
                {{ t('CAMPAIGN.WIZARD.SUMMARY.TEMPLATE') }}
              </span>
              <span class="text-sm font-medium text-n-slate-12">{{
                selectedTemplate?.name || '—'
              }}</span>
            </div>
            <div class="flex items-center justify-between px-4 py-3">
              <span class="text-sm text-n-slate-11">
                {{ t('CAMPAIGN.WIZARD.SUMMARY.AUDIENCE') }}
              </span>
              <span
                class="text-sm font-medium text-n-slate-12 text-right max-w-[60%] truncate"
                >{{ selectedSegmentNames }}</span
              >
            </div>
            <div class="flex items-center justify-between px-4 py-3">
              <span class="text-sm text-n-slate-11">
                {{ t('CAMPAIGN.WIZARD.SUMMARY.ESTIMATED_CONTACTS') }}
              </span>
              <span class="text-sm font-medium text-n-slate-12">{{
                totalContacts.toLocaleString(localeTag.value)
              }}</span>
            </div>
            <div class="flex items-center justify-between px-4 py-3">
              <span class="text-sm text-n-slate-11">
                {{ t('CAMPAIGN.WIZARD.SUMMARY.SCHEDULE') }}
              </span>
              <span class="text-sm font-medium text-n-slate-12">{{
                scheduleLabel
              }}</span>
            </div>
          </div>
        </div>

        <!-- Navegação -->
        <div class="flex items-center gap-3 mt-8">
          <Button
            v-if="currentStep < 4"
            :label="t('CAMPAIGN.WIZARD.ACTIONS.NEXT')"
            :disabled="!canProceed"
            @click="goNext"
          />
          <Button
            v-else
            :label="t('CAMPAIGN.WIZARD.ACTIONS.CREATE')"
            :disabled="!canProceed || isCreating"
            :is-loading="isCreating"
            @click="submit"
          />
          <Button
            variant="faded"
            color="slate"
            :label="
              currentStep === 1
                ? t('CAMPAIGN.WIZARD.ACTIONS.CANCEL')
                : t('CAMPAIGN.WIZARD.ACTIONS.BACK')
            "
            @click="goBack"
          />
        </div>
      </div>
    </div>
  </div>
</template>
